/**
 * Change scorecard — GitHub Issues and PRs graded for close/merge diligence.
 * super_admin only. Live GitHub is the system of record; this is the oversight view.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Icons } from '../common/Icons';
import { systemService } from '../../services/api';

const gradeClass = (grade) => {
  if (grade === 'fail') return 'badge badge-error';
  if (grade === 'warn') return 'badge badge-warning';
  return 'badge badge-success';
};

const ScoreTable = ({ rows, kind }) => {
  if (!rows.length) {
    return (
      <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0 }}>
        No {kind} in the scoring window.
      </p>
    );
  }
  return (
    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: '72px' }}>#</th>
            <th>Title</th>
            <th style={{ width: '90px' }}>State</th>
            <th style={{ width: '80px' }}>Grade</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.number}>
              <td>
                <a href={r.htmlUrl} target="_blank" rel="noreferrer">
                  {r.number}
                </a>
              </td>
              <td>{r.title}</td>
              <td>{r.state}</td>
              <td>
                <span className={gradeClass(r.grade)}>{r.grade}</span>
              </td>
              <td style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                {(r.notes || []).join(' ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const GithubScorecardView = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await systemService.getGithubScorecard();
      setData(res.data || res);
    } catch (err) {
      console.error('Failed to load GitHub scorecard', err);
      setError(err?.response?.data?.message || 'Failed to load the change scorecard.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="empty-state">
        <Icons.Loader size={48} className="spin" />
        <p>Loading change scorecard…</p>
        <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="alert alert-warning mb-3">
          <Icons.AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      <div className="card mb-3" style={{ padding: '16px' }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)' }}>
              Oversight scorecard for GitHub Issues and pull requests (about 90 days).
              Close comments must include <code>Disposition:</code>. Merged PRs need a checked
              Risk box and a <code>deployed / smoke OK</code> comment.
            </p>
            {data?.generatedAt && (
              <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                {data.owner}/{data.repo}
                {data.configured ? ` · generated ${data.generatedAt}` : ''}
              </p>
            )}
          </div>
          <button className="btn btn-secondary" onClick={load} disabled={loading}>
            <Icons.RefreshCw size={18} />
            Refresh
          </button>
        </div>
      </div>

      {data && !data.configured && (
        <div className="alert alert-warning mb-3">
          <Icons.Info size={18} />
          <span>
            {data.message || 'GitHub token not configured.'} Add{' '}
            <code>GITHUB_SCORECARD_TOKEN</code> to the Railway API service (fine-grained PAT:
            Issues read, Pull requests read), then refresh.
          </span>
        </div>
      )}

      {data && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon success">
                <Icons.CheckCircle size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{data.issues?.summary?.pass ?? 0}</div>
                <div className="stat-label">Issues pass</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon warning">
                <Icons.AlertCircle size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{data.issues?.summary?.fail ?? 0}</div>
                <div className="stat-label">Issues fail</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon success">
                <Icons.CheckCircle size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-value">{data.pullRequests?.summary?.pass ?? 0}</div>
                <div className="stat-label">PRs pass</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon warning">
                <Icons.AlertCircle size={24} />
              </div>
              <div className="stat-content">
                <div className="stat-value">
                  {(data.pullRequests?.summary?.warn ?? 0) + (data.pullRequests?.summary?.fail ?? 0)}
                </div>
                <div className="stat-label">PRs warn / fail</div>
              </div>
            </div>
          </div>

          <div className="card mb-3">
            <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>Issues</h3>
            <ScoreTable rows={data.issues?.rows || []} kind="issues" />
          </div>

          <div className="card">
            <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>Pull requests</h3>
            <ScoreTable rows={data.pullRequests?.rows || []} kind="pull requests" />
          </div>
        </>
      )}
    </div>
  );
};

export default GithubScorecardView;
